import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/authStore'

export function usePropPick(propPickId) {
  return useQuery({
    queryKey: ['propPicks', propPickId],
    queryFn: () => api.get(`/props/picks/${propPickId}`),
    enabled: !!propPickId,
  })
}

// User-facing props tab: fetches today's slate of props for one (sport,
// market) via the server-side loader which upserts+publishes rows and
// returns them enriched with headshots + game metadata. Server-side cache
// (5-min TTL) absorbs the traffic across users; client-side we hold each
// (sport, market) fresh for 60s so quickly toggling groups doesn't hammer
// the endpoint. First tap on a chip pays the round-trip; re-taps within
// the window are instant from React Query cache.
export function useLoadedProps(sport, market, { enabled = true } = {}) {
  return useQuery({
    queryKey: ['loadedProps', sport, market],
    queryFn: () => api.get(`/props/load?sport=${encodeURIComponent(sport)}&market=${encodeURIComponent(market)}`),
    enabled: enabled && !!sport && !!market,
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}

export function useFeaturedProps(date, { fallback = false } = {}) {
  return useQuery({
    queryKey: ['featuredProps', date, fallback],
    queryFn: () => api.get(`/props/featured?date=${date}${fallback ? '&fallback=true' : ''}`),
    enabled: !!date,
    // Mirror useGames: poll every 30s so props whose games have just kicked
    // off get dropped from the Picks page on the same cadence games do.
    // Without this the client-side `starts_at <= now` filter sits inside a
    // useMemo that only re-evaluates when `props` change — which means a
    // prop loaded before its game started would linger on the page until
    // navigation. Refetching gives the server a chance to also report the
    // prop as `locked`, which the filter catches independently.
    refetchInterval: 30_000,
  })
}

export function useMyPropPicks(status) {
  const params = status ? `?status=${status}` : ''
  return useQuery({
    queryKey: ['propPicks', 'me', status],
    queryFn: () => api.get(`/props/picks/me${params}`),
    // The list query is now cheap (no ESPN round-trips) and only needs to
    // catch status transitions, so we can poll it more conservatively. Live
    // stats are served by useMyPropLiveStats on its own 30s cadence.
    refetchInterval: (query) => {
      const hasLive = query.state.data?.some((p) => p.status === 'locked' && p.player_props?.games?.status === 'live')
      return hasLive ? 120000 : undefined
    },
  })
}

export function usePropPickHistory() {
  return useQuery({
    queryKey: ['propPicks', 'history'],
    queryFn: () => api.get('/props/picks/me/history'),
    refetchInterval: (query) => {
      const hasLocked = query.state.data?.some((p) => p.status === 'locked')
      const hasLive = query.state.data?.some((p) => p.status === 'locked' && p.player_props?.games?.status === 'live')
      // When games are live, poll faster to catch settlements; when there are
      // locked picks but no live games yet, poll to catch game status transitions
      return hasLive ? 60000 : hasLocked ? 120000 : undefined
    },
  })
}

/**
 * Live prop-pick stats — polled separately from the pick list so the (heavier)
 * ESPN fallback only runs on this cadence, not on every list refetch. Returns
 * a `{ [pickId]: number }` map. Caller merges into picks at render time.
 *
 * `enabled` is gated on whether the caller actually has any locked picks
 * with a live game, so this stays free for users with no active picks.
 */
export function useMyPropLiveStats({ hasLive } = {}) {
  return useQuery({
    queryKey: ['propPicks', 'liveStats'],
    queryFn: () => api.get('/props/picks/me/live-stats'),
    enabled: !!hasLive,
    refetchInterval: hasLive ? 30000 : undefined,
  })
}

export function useSubmitPropPick() {
  const queryClient = useQueryClient()

  return useMutation({
    // chosenLine is only sent for slider markets. Omitted entirely otherwise
    // so flat markets keep posting exactly the body they posted before.
    mutationFn: ({ propId, pickedSide, chosenLine }) =>
      api.post('/props/picks', {
        prop_id: propId,
        picked_side: pickedSide,
        ...(chosenLine != null ? { chosen_line: chosenLine } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propPicks'] })
      const uid = useAuthStore.getState().session?.user?.id
      if (uid) localStorage.setItem(`ikb_welcome_first_pick_${uid}`, '1')
    },
  })
}

export function useDeletePropPick() {
  const queryClient = useQueryClient()

  return useMutation({
    // Accepts a bare propId (every pending pick on it) or { propId, pickId }
    // for one bet. The bare form is what the over/under card still sends.
    mutationFn: (arg) => {
      const { propId, pickId } = typeof arg === 'object' && arg !== null ? arg : { propId: arg }
      return api.delete(`/props/picks/${propId}${pickId ? `?pickId=${pickId}` : ''}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propPicks'] })
    },
  })
}
